import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

const DRAG_THRESHOLD = 6;
const EDITOR_MODAL_STATE_KEY = 'header-marquee-modal-state';

/** @typedef {typeof globalThis & { __headerMarqueeModalFallbackInitialized?: boolean }} MarqueeWindow */
/**
 * @typedef {object} HeaderMarqueeModalItem
 * @property {string} titleHtml
 * @property {string} titleText
 * @property {string} informationHtml
 * @property {string} linksHtml
 * @property {string} titleFont
 * @property {string} titleSize
 * @property {string} titleSizeMobile
 * @property {string} titleCase
 * @property {string} infoFont
 * @property {string} infoSize
 * @property {string} infoSizeMobile
 * @property {string} infoCase
 * @property {string} linksFont
 * @property {string} linksSize
 * @property {string} linksSizeMobile
 * @property {string} linksCase
 */

const CLOSE_ICON_SVG = `<svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M12 2L2 12" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M12 12L2 2" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;
/**
 * A custom element that displays a marquee.
 * Supports an optional drag interaction when `data-draggable` is present.
 * Dragging pauses the running animation and scrubs its timeline horizontally.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    this.setAttribute('data-disabled', '');

    const { numberOfCopies } = await this.#queryNumberOfCopies();

    const speed = this.#calculateSpeed(numberOfCopies);

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();

    this.#setSpeed(speed);
    this.removeAttribute('data-disabled');

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);

    if (this.#isDraggable) {
      this.addEventListener('pointerdown', this.#onDragStart);
      this.addEventListener('click', this.#onClickCapture, true);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
    this.removeEventListener('pointerdown', this.#onDragStart);
    this.removeEventListener('pointermove', this.#onDragMove);
    this.removeEventListener('pointerup', this.#onDragEnd);
    this.removeEventListener('pointercancel', this.#onDragEnd);
    this.removeEventListener('click', this.#onClickCapture, true);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  /** @type {number} */
  #dragStartX = 0;

  /** @type {number} */
  #dragStartAnimTime = 0;

  /** @type {boolean} */
  #isDragging = false;

  /** @type {boolean} */
  #isPointerDown = false;

  /** @type {number | null} */
  #dragPointerId = null;

  /** @type {boolean} */
  #suppressClick = false;

  /**
   * Drag mode is opt-in so existing marquees keep the original hover-only behavior.
   * @returns {boolean}
   */
  get #isDraggable() {
    return this.hasAttribute('data-draggable');
  }

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        // Wrapping the resolve in a setTimeout here and below splits each marquee reflow into a separate task.
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          setTimeout(() => {
            resolve({
              numberOfCopies: marqueeItemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeItemsWidth),
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;

    return speed;
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    const { numberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    // opt out of marquee manipulation on vertical resizes
    if (!isHorizontalResize) return;

    const currentNumberOfCopies = marqueeItems.length;
    const speed = this.#calculateSpeed(numberOfCopies);

    if (numberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(numberOfCopies - currentNumberOfCopies);
    } else if (numberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - numberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = marqueeItems[0].cloneNode(true);
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }

  /**
   * Freeze the current marquee animation and begin pointer-driven scrubbing.
   * @param {PointerEvent} event
   */
  #onDragStart = (event) => {
    if (event.button !== undefined && event.button !== 0) return;

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    this.#isPointerDown = true;
    this.#isDragging = false;
    this.#dragPointerId = event.pointerId;
    this.#dragStartX = event.clientX;
    this.#dragStartAnimTime = Number(animation.currentTime ?? 0);

    this.addEventListener('pointermove', this.#onDragMove);
    this.addEventListener('pointerup', this.#onDragEnd);
    this.addEventListener('pointercancel', this.#onDragEnd);
  };

  /**
   * Convert horizontal pointer movement into animation timeline movement.
   * @param {PointerEvent} event
   */
  #onDragMove = (event) => {
    if (!this.#isPointerDown || this.#dragPointerId !== event.pointerId) return;

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    const dx = event.clientX - this.#dragStartX;

    if (!this.#isDragging) {
      if (Math.abs(dx) < DRAG_THRESHOLD) return;

      event.preventDefault();
      this.#isDragging = true;
      this.#suppressClick = true;

      this.#slowDown.cancel();
      this.#animation?.cancel();
      this.#animation = null;

      animation.pause();
      this.setPointerCapture(event.pointerId);
      this.style.cursor = 'grabbing';
    }

    event.preventDefault();
    const halfWidth = this.refs.wrapper.scrollWidth / 2;
    const timing = animation.effect?.getTiming();
    if (!timing || halfWidth === 0) return;

    const duration = Number(timing.duration ?? 0);
    if (duration === 0) return;

    const direction = this.getAttribute('data-movement-direction');
    const sign = direction === 'reverse' ? 1 : -1;
    const timeDelta = sign * (dx / halfWidth) * duration;

    let newTime = (this.#dragStartAnimTime + timeDelta) % duration;
    if (newTime < 0) newTime += duration;

    animation.currentTime = newTime;
  };

  /**
   * Release drag state and resume automatic marquee playback.
   * @param {PointerEvent} event
   */
  #onDragEnd = (event) => {
    if (this.#dragPointerId !== event.pointerId) return;

    this.#isPointerDown = false;
    this.#dragPointerId = null;
    this.removeEventListener('pointermove', this.#onDragMove);
    this.removeEventListener('pointerup', this.#onDragEnd);
    this.removeEventListener('pointercancel', this.#onDragEnd);

    if (!this.#isDragging) return;

    this.#isDragging = false;
    setTimeout(() => {
      this.#suppressClick = false;
    }, 0);

    this.style.cursor = '';
    if (this.hasPointerCapture?.(event.pointerId)) {
      this.releasePointerCapture(event.pointerId);
    }

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    animation.updatePlaybackRate(1);
    animation.play();
  };

  /**
   * Suppress synthetic click activation after a real drag gesture.
   * @param {MouseEvent} event
   */
  #onClickCapture = (event) => {
    if (!this.#suppressClick) return;

    this.#suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  };
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

/**
 * @param {EventTarget | null} target
 * @param {string} selector
 * @returns {Element | null}
 */
function getClosestElement(target, selector) {
  return target instanceof Element ? target.closest(selector) : null;
}

/**
 * @param {string | null} modalId
 * @returns {HTMLElement | null}
 */
function getModalElement(modalId) {
  if (!modalId) return null;
  const element = document.getElementById(modalId);
  return element instanceof HTMLElement ? element : null;
}

/**
 * @param {Element | null} element
 * @returns {HTMLDivElement | null}
 */
function getModalPanel(element) {
  if (!(element instanceof HTMLElement)) return null;
  const panel = element.querySelector('.hm-modal__panel');
  return panel instanceof HTMLDivElement ? panel : null;
}

/**
 * @param {Element} element
 * @param {string} attributeName
 * @returns {string}
 */
function getTrimmedAttribute(element, attributeName) {
  return element.getAttribute(attributeName)?.trim() ?? '';
}

/**
 * @param {Element | null} element
 * @returns {HTMLElement | null}
 */
function getMarqueeModalRoot(element) {
  if (!(element instanceof Element)) return null;
  const root = element.closest('.header-marquee, .hmw');
  return root instanceof HTMLElement ? root : null;
}

/**
 * @param {Element | null} element
 * @returns {boolean}
 */
function isModalEnabledForElement(element) {
  const root = getMarqueeModalRoot(element);
  return root?.dataset.hmModalEnabled === 'true';
}

/**
 * @param {Element | null} element
 * @returns {string}
 */
function getModalIdForElement(element) {
  const root = getMarqueeModalRoot(element);
  return root?.dataset.hmModalId ?? '';
}

/**
 * Builds the CSS class applied to the JS-built modal panel.
 * Zooyork does not use Shopify-native color scheme classes; instead the
 * liquid layer emits a `.color-custom-{block.id}-modal` rule (via
 * `contrast-override`) whenever a modal background color is set. Derive the
 * block id from `data-hm-modal-id` (format: `hm-modal-<blockId>`) and only
 * apply the class when a background color is actually present.
 * @param {HTMLElement} trigger
 * @returns {string}
 */
function getModalColorSchemeClass(trigger) {
  const root = getMarqueeModalRoot(trigger);
  if (!root) return '';

  const modalId = root.dataset.hmModalId ?? '';
  const modalIdPrefix = 'hm-modal-';
  if (!modalId.startsWith(modalIdPrefix)) return '';

  const blockId = modalId.slice(modalIdPrefix.length);
  if (!blockId) return '';

  const backgroundColor = root.dataset.hmModalBackgroundColor?.trim();
  if (!backgroundColor) return '';

  return `color-custom-${blockId}-modal`;
}

/**
 * @returns {boolean}
 */
function isThemeEditor() {
  return Boolean(window.Shopify?.designMode);
}

/**
 * @returns {string | null}
 */
function getEditorModalState() {
  return sessionStorage.getItem(EDITOR_MODAL_STATE_KEY);
}

/**
 * @param {string} modalId
 * @returns {void}
 */
function setEditorModalState(modalId) {
  if (!isThemeEditor()) return;
  sessionStorage.setItem(EDITOR_MODAL_STATE_KEY, modalId);
}

/**
 * @returns {void}
 */
function clearEditorModalState() {
  if (!isThemeEditor()) return;
  sessionStorage.removeItem(EDITOR_MODAL_STATE_KEY);
}

/**
 * @param {HeaderMarqueeModalItem} item
 * @returns {string}
 */
function buildModalItemStyle(item) {
  return [
    item.titleFont ? `--hm-modal-title-font: var(${item.titleFont});` : '',
    item.titleSize ? `--hm-modal-title-size: ${item.titleSize}px;` : '',
    item.titleSizeMobile ? `--hm-modal-title-size-mob: ${item.titleSizeMobile}px;` : '',
    item.titleCase ? `--hm-modal-title-case: ${item.titleCase};` : '',
    item.infoFont ? `--hm-modal-info-font: var(${item.infoFont});` : '',
    item.infoSize ? `--hm-modal-info-size: ${item.infoSize}px;` : '',
    item.infoSizeMobile ? `--hm-modal-info-size-mob: ${item.infoSizeMobile}px;` : '',
    item.infoCase ? `--hm-modal-info-case: ${item.infoCase};` : '',
    item.linksFont ? `--hm-modal-links-font: var(${item.linksFont});` : '',
    item.linksSize ? `--hm-modal-links-size: ${item.linksSize}px;` : '',
    item.linksSizeMobile ? `--hm-modal-links-size-mob: ${item.linksSizeMobile}px;` : '',
    item.linksCase ? `--hm-modal-links-case: ${item.linksCase};` : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * @param {HTMLElement | null} root
 * @returns {HTMLElement[]}
 */
function getModalSourceItems(root) {
  if (!(root instanceof HTMLElement)) return [];

  const repeatedGroup = root.querySelector('.marquee__content > .marquee__repeated-items');
  if (repeatedGroup instanceof HTMLElement) {
    return Array.from(repeatedGroup.querySelectorAll('.header-marquee-item')).filter(
      (item) => item instanceof HTMLElement
    );
  }

  const announcementSlides = root.querySelector('.header-marquee__announcement-slides, .hmw__announcement-slides');
  if (!(announcementSlides instanceof HTMLElement)) return [];

  /** @type {HTMLElement[]} */
  const announcementItems = [];

  for (const item of Array.from(announcementSlides.children)) {
    if (item instanceof HTMLElement && item.classList.contains('header-marquee-item')) {
      announcementItems.push(item);
    }
  }

  return announcementItems;
}

/**
 * @param {HTMLElement} trigger
 * @returns {HeaderMarqueeModalItem[]}
 */
function getHeaderMarqueeModalItems(trigger) {
  const root = getMarqueeModalRoot(trigger);
  const sourceItems = getModalSourceItems(root);

  return sourceItems
    .map((item) => {
      const title =
        getTrimmedAttribute(item, 'data-modal-title');
      const titleText = item.querySelector('.header-marquee-item__text')?.textContent?.trim() || '';

      return {
        titleHtml: title,
        titleText,
        informationHtml: getTrimmedAttribute(item, 'data-modal-information'),
        linksHtml: getTrimmedAttribute(item, 'data-modal-links'),
        titleFont: getTrimmedAttribute(item, 'data-modal-title-font'),
        titleSize: getTrimmedAttribute(item, 'data-modal-title-size'),
        titleSizeMobile: getTrimmedAttribute(item, 'data-modal-title-size-mobile'),
        titleCase: getTrimmedAttribute(item, 'data-modal-title-case'),
        infoFont: getTrimmedAttribute(item, 'data-modal-info-font'),
        infoSize: getTrimmedAttribute(item, 'data-modal-info-size'),
        infoSizeMobile: getTrimmedAttribute(item, 'data-modal-info-size-mobile'),
        infoCase: getTrimmedAttribute(item, 'data-modal-info-case'),
        linksFont: getTrimmedAttribute(item, 'data-modal-links-font'),
        linksSize: getTrimmedAttribute(item, 'data-modal-links-size'),
        linksSizeMobile: getTrimmedAttribute(item, 'data-modal-links-size-mobile'),
        linksCase: getTrimmedAttribute(item, 'data-modal-links-case'),
      };
    })
    .filter((item) => item.titleHtml || item.titleText || item.informationHtml || item.linksHtml);
}

/**
 * @param {HTMLElement} trigger
 * @returns {HTMLElement | null}
 */
function ensureHeaderMarqueeModal(trigger) {
  const modalId = trigger.getAttribute('data-opens-modal') || getModalIdForElement(trigger);
  if (!modalId) return null;

  const existingModal = getModalElement(modalId);
  if (existingModal) {
    if (existingModal.dataset.hmModalFallback === 'true' && isThemeEditor()) {
      existingModal.remove();
    } else {
      return existingModal;
    }
  }

  const items = getHeaderMarqueeModalItems(trigger);
  if (items.length === 0) return null;

  const colorSchemeClass = getModalColorSchemeClass(trigger);

  const modal = document.createElement('div');
  modal.className = 'hm-modal';
  modal.id = modalId;
  modal.dataset.hmModalFallback = 'true';
  modal.setAttribute('role', 'dialog');
  modal.setAttribute('aria-modal', 'true');
  modal.setAttribute('aria-label', 'Informacion');
  modal.setAttribute('hidden', '');
  modal.innerHTML = `
    <div class="hm-modal__backdrop" aria-hidden="true"></div>
    <div class="hm-modal__panel${colorSchemeClass ? ` ${colorSchemeClass}` : ''}">
      <button class="button button-unstyled close-button hm-modal__close" type="button" aria-label="Cerrar dialogo">
        ${CLOSE_ICON_SVG}
      </button>
      <div class="hm-modal__grid" style="--hm-modal-column-count: ${Math.min(items.length, 3)};">
        ${items
          .map(
            (item) => `
              <div class="hm-modal__item" style="${buildModalItemStyle(item)}">
                ${
                  item.titleHtml || item.titleText
                    ? `<p class="hm-modal__title">${item.titleHtml || escapeHtml(item.titleText)}</p>`
                    : ''
                }
                ${item.informationHtml ? `<div class="hm-modal__info rte">${item.informationHtml}</div>` : ''}
                ${item.linksHtml ? `<div class="hm-modal__links rte">${item.linksHtml}</div>` : ''}
              </div>
            `
          )
          .join('')}
      </div>
    </div>
  `;

  document.body.appendChild(modal);
  return modal;
}

/**
 * @param {HTMLElement} trigger
 * @returns {void}
 */
function openHeaderMarqueeModal(trigger) {
  const modal = ensureHeaderMarqueeModal(trigger);
  if (!modal) return;

  modal.removeAttribute('hidden');
  modal.setAttribute('open', '');
  setEditorModalState(modal.id);
  document.documentElement.style.overflow = 'hidden';

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      modal.classList.add('is-open');
    });
  });
}

/**
 * @param {Element | null} modal
 * @returns {void}
 */
function closeHeaderMarqueeModal(modal) {
  if (!(modal instanceof HTMLElement) || modal.hasAttribute('hidden')) return;

  const panel = getModalPanel(modal);
  modal.classList.remove('is-open');

  const finishClose = () => {
    modal.setAttribute('hidden', '');
    modal.removeAttribute('open');
    clearEditorModalState();
    document.documentElement.style.overflow = '';
  };

  if (!panel) {
    finishClose();
    return;
  }

  panel.addEventListener('transitionend', finishClose, { once: true });
}

/**
 * @returns {void}
 */
function initHeaderMarqueeModalFallback() {
  /** @type {MarqueeWindow} */
  const scopedWindow = window;

  if (scopedWindow.__headerMarqueeModalFallbackInitialized) return;
  scopedWindow.__headerMarqueeModalFallbackInitialized = true;

  /**
   * @param {ParentNode} [root]
   * @returns {void}
   */
  const initModalTriggers = (root = document) => {
    const modalRoots = Array.from(root.querySelectorAll('.header-marquee, .hmw'));

    for (const modalRoot of modalRoots) {
      if (!(modalRoot instanceof HTMLElement) || modalRoot.dataset.hmModalEnabled !== 'true') continue;

      const modalId = modalRoot.dataset.hmModalId;
      if (!modalId) continue;

      const items = modalRoot.querySelectorAll('.header-marquee-item');
      for (const item of items) {
        if (!(item instanceof HTMLElement)) continue;
        item.dataset.opensModal = modalId;
        item.classList.add('header-marquee-item--modal');
        item.setAttribute('role', 'button');
        item.setAttribute('tabindex', '0');
        item.setAttribute('aria-haspopup', 'dialog');
      }
    }
  };

  initModalTriggers();

  /** @param {MouseEvent} event */
  document.addEventListener('click', (event) => {
    const closeButton = getClosestElement(event.target, '.hm-modal__close');
    if (closeButton) {
      closeHeaderMarqueeModal(closeButton.closest('.hm-modal'));
      return;
    }

    const backdrop = getClosestElement(event.target, '.hm-modal__backdrop');
    if (backdrop) {
      closeHeaderMarqueeModal(backdrop.closest('.hm-modal'));
      return;
    }

    const trigger = getClosestElement(event.target, '[data-opens-modal]');
    if (!(trigger instanceof HTMLElement) || !isModalEnabledForElement(trigger)) return;

    const existingModal = getModalElement(trigger.getAttribute('data-opens-modal'));
    // Reabrir si no existe o si es un modal fallback (el widget no tiene handler
    // server-side; sin esto no reabre tras cerrar). Los modales server-side de la
    // sección (sin data-hm-modal-fallback) los maneja su propio handler.
    if (!existingModal || existingModal.dataset.hmModalFallback === 'true') {
      event.preventDefault();
      openHeaderMarqueeModal(trigger);
    }
  });

  /** @param {KeyboardEvent} event */
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      const openModal = document.querySelector('.hm-modal.is-open');
      if (openModal) closeHeaderMarqueeModal(openModal);
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') return;

    const trigger = getClosestElement(event.target, '[data-opens-modal]');
    if (!(trigger instanceof HTMLElement) || !isModalEnabledForElement(trigger)) return;

    const existingModal = getModalElement(trigger.getAttribute('data-opens-modal'));
    // Reabrir si no existe o si es un modal fallback (el widget no tiene handler
    // server-side; sin esto no reabre tras cerrar). Los modales server-side de la
    // sección (sin data-hm-modal-fallback) los maneja su propio handler.
    if (!existingModal || existingModal.dataset.hmModalFallback === 'true') {
      event.preventDefault();
      openHeaderMarqueeModal(trigger);
    }
  });

  if (isThemeEditor()) {
    /**
     * @returns {void}
     */
    const restoreOpenModal = () => {
      const modalId = getEditorModalState();
      if (!modalId) return;

      const trigger = document.querySelector(`[data-opens-modal="${modalId}"]`);
      if (!(trigger instanceof HTMLElement)) return;

      openHeaderMarqueeModal(trigger);
    };

    document.addEventListener('shopify:section:load', () => {
      initModalTriggers();
      setTimeout(restoreOpenModal, 0);
    });

    setTimeout(restoreOpenModal, 0);
  }
}

initHeaderMarqueeModalFallback();

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
